import { getAuthenticationToken } from "../../auth/helpers";
import { buildConfigForEnvironment } from "../config";
import { GetFeatureFlagsResponse, FeatureFlagName } from "./types";

// Set VITE_USE_MOCK_FEATURE_FLAGS=true in .env.local to use mock flags locally
const USE_MOCK_FLAGS = import.meta.env.VITE_USE_MOCK_FEATURE_FLAGS === "true";

const getMockFlags = (): Record<string, boolean> => ({
  enableIplClaims: import.meta.env.VITE_FEATURE_FLAG_ENABLE_IPL_CLAIMS === "true",
  enableWcCanadaClaims: import.meta.env.VITE_FEATURE_FLAG_ENABLE_WC_CANADA_CLAIMS === "true",
  enableCargoJsonForms: import.meta.env.VITE_FEATURE_FLAG_ENABLE_CARGO_JSON_FORMS === "true",
  enableJsonForms: import.meta.env.VITE_FEATURE_FLAG_ENABLE_JSON_FORMS === "true",
  enableFormStudio: import.meta.env.VITE_FEATURE_FLAG_ENABLE_FORM_STUDIO === "true",
});

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const FETCH_TIMEOUT_MS = 3000; // 3 seconds

interface CachedFlags {
  flags: Record<string, boolean>;
  timestamp: number;
}

let cachedFlags: CachedFlags | null = null;
let inFlightRequest: Promise<Record<string, boolean>> | null = null;

const isCacheValid = (): boolean => {
  if (!cachedFlags) return false;
  return Date.now() - cachedFlags.timestamp < CACHE_TTL_MS;
};

export const getCachedFlags = (): Record<string, boolean> | null => {
  return cachedFlags?.flags ?? null;
};

/**
 * Returns true once feature flags have been successfully loaded into the
 * module-scoped cache (either from the network or from mock flags).
 *
 * Use this to distinguish "flags failed / haven't finished loading" from
 * "flag is explicitly false". Without this check, consumers reading flags
 * via getCachedFlags()/FeatureFlags would silently fall back to false
 * during the loading window or after a fetch failure.
 */
export const areFeatureFlagsLoaded = (): boolean => cachedFlags !== null;

// TODO: Replace with Smithy client once GetFeatureFlagsCommand is available
export const fetchFeatureFlags = async (): Promise<Record<string, boolean>> => {
  if (isCacheValid() && cachedFlags) {
    return cachedFlags.flags;
  }

  // Deduplicate concurrent requests
  if (inFlightRequest) {
    return inFlightRequest;
  }

  inFlightRequest = doFetchFeatureFlags();
  try {
    return await inFlightRequest;
  } finally {
    inFlightRequest = null;
  }
};

const doFetchFeatureFlags = async (): Promise<Record<string, boolean>> => {
  if (USE_MOCK_FLAGS) {
    const flags = getMockFlags();
    cachedFlags = { flags, timestamp: Date.now() };
    return flags;
  }

  const token = await getAuthenticationToken();
  if (!token) {
    if (cachedFlags?.flags) {
      return cachedFlags.flags;
    }
    throw new Error("No authentication token available");
  }

  const config = buildConfigForEnvironment();
  const endpoint = `${config.endpoint}/getFeatureFlags`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch feature flags: ${response.status}`);
    }

    const data: GetFeatureFlagsResponse = await response.json();
    const flags = data.flags ?? {};
    cachedFlags = { flags, timestamp: Date.now() };
    return flags;
  } catch (error) {
    console.error("Error fetching feature flags:", error);
    // Return cached flags if available, otherwise re-throw so consumers can handle
    if (cachedFlags?.flags) {
      return cachedFlags.flags;
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getFeatureFlag = async (
  flagName: FeatureFlagName,
  defaultValue = false
): Promise<boolean> => {
  try {
    const flags = await fetchFeatureFlags();
    return flags[flagName] ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

export const clearFeatureFlagsCache = (): void => {
  cachedFlags = null;
  inFlightRequest = null;
};
